/**
 * Captioned screenshots and screen recordings, with a click-to-open overlay.
 *
 * The caption under each item is the reporter's kuvateksti, not a filename —
 * that is what makes a multi-attachment report readable.
 *
 * Video is deliberately NOT rendered inline as a <video>: five of them in a
 * thread would each start fetching, and a poster frame plus a duration badge
 * conveys everything needed to decide whether to watch. Playback happens in the
 * overlay, where exactly one clip is loaded at a time.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useAttachmentUrl } from "@/hooks/useAttachmentUrl";
import { formatDuration } from "@/lib/feedbackMeta";
import { CloseIcon, ImageIcon } from "@/lib/icons";
import type { FeedbackAttachment } from "@/types";

interface MediaGalleryProps {
  attachments: FeedbackAttachment[];
}

/** Grid thumbnail. Its own component so the hook is not called in a loop. */
function GalleryThumb({ attachment }: { attachment: FeedbackAttachment }) {
  const { url, onError } = useAttachmentUrl(attachment.thumbUrl);

  // Doubles as the loading state and as the no-poster-frame case: a clip whose
  // codec the browser could not decode has no frame to show for it.
  if (!url) {
    return (
      <span className="grid h-[116px] w-[176px] place-items-center bg-muted">
        <ImageIcon className="h-6 w-6 text-muted-foreground" />
      </span>
    );
  }

  return (
    <img
      src={url}
      alt={attachment.caption ?? attachment.filename}
      loading="lazy"
      onError={onError}
      className="h-[116px] w-[176px] object-cover"
    />
  );
}

export function MediaGallery({ attachments }: MediaGalleryProps) {
  const [opened, setOpened] = useState<FeedbackAttachment | null>(null);
  const openedMedia = useAttachmentUrl(opened?.url ?? null);
  const openedPoster = useAttachmentUrl(opened?.thumbUrl ?? null);

  useEffect(() => {
    if (!opened) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Capture phase + stopPropagation: the enclosing report Dialog also closes
      // on Escape, so without this one keypress would dismiss the viewer AND the
      // whole card underneath it.
      event.stopPropagation();
      event.preventDefault();
      setOpened(null);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [opened]);

  if (attachments.length === 0) return null;

  const isVideo = opened?.mediaType === "VIDEO";

  return (
    <>
      <div className="flex flex-wrap gap-2.5">
        {attachments.map((attachment) => (
          <figure key={attachment.id} className="m-0 max-w-[11rem]">
            <button
              type="button"
              onClick={() => setOpened(attachment)}
              aria-label={
                attachment.mediaType === "VIDEO"
                  ? `Toista video: ${attachment.caption ?? attachment.filename}`
                  : `Avaa kuva: ${attachment.caption ?? attachment.filename}`
              }
              className="relative block cursor-pointer overflow-hidden rounded-lg border leading-none"
            >
              <GalleryThumb attachment={attachment} />

              {attachment.mediaType === "VIDEO" && (
                <>
                  <span className="absolute inset-0 grid place-items-center bg-black/25">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-black/65 pl-0.5 text-sm text-white">
                      ▶
                    </span>
                  </span>
                  <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none text-white">
                    {formatDuration(attachment.durationSeconds) || "video"}
                  </span>
                </>
              )}
            </button>
            {attachment.caption && (
              <figcaption className="pt-1 text-[11px] text-muted-foreground">
                {attachment.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>

      {/* Portalled to <body> on purpose. DialogContent carries
          translate-x-[-50%], and a transform makes a containing block for
          fixed-position descendants — so rendered in place, this overlay would
          size itself to the dialog box rather than the viewport, and the image
          would appear cropped and zoomed inside a scrolling dialog. */}
      {opened &&
        createPortal(
          <div
            // The backdrop still covers the viewport — it has to, to catch the
            // click-outside — but the viewer itself is a modest centred window
            // rather than edge-to-edge.
            //
            // Only the backdrop closes: a video swallows clicks on its own
            // controls, so pressing play must not dismiss the viewer. Clicking
            // the panel makes the panel the event target, not the backdrop, so
            // the identity check below covers that on its own. stopPropagation
            // keeps the click from reaching the report Dialog's outside-click
            // handler, which would otherwise close the card too.
            onClick={(event) => {
              event.stopPropagation();
              if (event.target === event.currentTarget) setOpened(null);
            }}
            // pointer-events-auto is load-bearing, not decoration. The enclosing
            // Radix Dialog is modal, so it sets `pointer-events: none` on
            // document.body and re-enables it only inside its own content. This
            // overlay is portalled to body, so without this it inherits none and
            // every click — including the close button — falls straight through
            // to the Dialog overlay beneath, which closes the whole card.
            className="pointer-events-auto fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4"
          >
            <div className="relative flex max-h-[70vh] max-w-2xl flex-col overflow-hidden rounded-lg bg-card shadow-2xl">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpened(null);
                }}
                aria-label="Sulje kuva"
                title="Sulje"
                className="absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-md bg-black/50 text-white opacity-80 hover:bg-black/70 hover:opacity-100"
              >
                <CloseIcon className="h-4 w-4" />
              </button>

              {!openedMedia.url ? (
                <span className="grid h-[220px] w-[320px] place-items-center bg-muted">
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                </span>
              ) : isVideo ? (
                <video
                  src={openedMedia.url}
                  poster={openedPoster.url ?? undefined}
                  controls
                  autoPlay
                  // metadata, not auto: the signed URL points straight at the
                  // blob, and there is no reason to pull the whole clip before
                  // the viewer presses play.
                  preload="metadata"
                  onError={openedMedia.onError}
                  className="min-h-0 bg-black object-contain"
                />
              ) : (
                <img
                  src={openedMedia.url}
                  alt={opened.caption ?? opened.filename}
                  onError={openedMedia.onError}
                  // min-h-0 lets the flex child shrink so object-contain can fit
                  // the image inside max-h-[70vh] instead of overflowing it.
                  className="min-h-0 object-contain"
                />
              )}

              {opened.caption && (
                <figcaption className="shrink-0 border-t px-3 py-2 text-sm text-muted-foreground">
                  {opened.caption}
                </figcaption>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
